import { useState } from 'react';
import type { ApropriacaoEntrada, AtualizarContaContabilDto, AtualizarItemCifDto, ContaContabilResponse, CriarContaContabilDto, CriarItemCifDto, ItemCifResponse } from '@triade/contracts';
import { useAnaliticos } from '@/api/centros-custo';
import { useAtualizarContaContabil, useAtualizarItemCif, useContasContabeis, useCriarContaContabil, useCriarItemCif } from '@/api/itens-cif';
import { ApropriacaoPorCentro, type ErroApropriacao } from '@/components/apropriacao';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ApiError } from '@/lib/api';
import {
  apropriacaoInicial,
  deItem,
  itemVazio,
  montarCorpoConta,
  montarCorpoItem,
  type ContaFormEntrada,
  type ErroCampoForm,
  type ItemFormEntrada,
} from './item-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');
/** Erro de conta: a mensagem da API sozinha (ex.: "Já existe uma conta contábil com o código X"). */
const mensagemConta = (e: unknown) => (e instanceof ApiError ? e.message : 'falha de comunicação com a API');

/** Conta criada na tela, ainda nao gravada: vira conta de verdade (POST) ao salvar o item. `criadoId` evita criar de novo em nova tentativa. */
interface ContaNova extends ContaFormEntrada {
  tmp: string;
  criadoId: string | null;
  erro: string | null;
}

/** Linha de conta existente em edicao inline. */
interface ContaEmEdicao {
  id: string;
  codigo: string;
  descricao: string;
  erro: string | null;
}

/**
 * Item de CIF (recorrente): nome, contas contabeis (editaveis aqui), destino (apropriacao padrao ou atalho "vai direto para o centro").
 * `item` null = novo. Sem `rateio.editar` o formulario e so de leitura. Nada e calculado aqui: a apropriacao vem da API.
 */
export function ItemCifDialog({ item, podeEditar, onFechar }: { item: ItemCifResponse | null; podeEditar: boolean; onFechar: () => void }) {
  const criar = useCriarItemCif();
  const atualizar = useAtualizarItemCif();
  const criarConta = useCriarContaContabil();
  const atualizarConta = useAtualizarContaContabil();
  const contas = useContasContabeis({ ativo: true, limit: 200 });
  const centros = useAnaliticos(false, podeEditar);
  const [valor, setValor] = useState<ItemFormEntrada>(() => (item ? deItem(item) : itemVazio()));
  /** Apropriacao editada (null = nada editado). `completa` vem do componente. */
  const [apropriacao, setApropriacao] = useState<{ valor: ApropriacaoEntrada | null; completa: boolean } | null>(null);
  const [erroCampo, setErroCampo] = useState<ErroCampoForm | null>(null);
  const [erroApropriacao, setErroApropriacao] = useState<ErroApropriacao | null>(null);
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [contasNovas, setContasNovas] = useState<ContaNova[]>([]);
  const [contaEmEdicao, setContaEmEdicao] = useState<ContaEmEdicao | null>(null);
  const [modoAdicionar, setModoAdicionar] = useState<'existente' | 'nova' | null>(null);
  const [contaEscolhida, setContaEscolhida] = useState('');
  const [novaConta, setNovaConta] = useState<{ codigo: string; descricao: string; erro: string | null }>({ codigo: '', descricao: '', erro: null });
  const salvando = criar.isPending || atualizar.isPending || criarConta.isPending || atualizarConta.isPending;
  const criacao = item === null;

  /** Todas as contas conhecidas nesta tela (ativas + as ja vinculadas ao item, mesmo inativas). */
  const contaPorId = new Map<string, ContaContabilResponse>();
  for (const c of contas.data ?? []) contaPorId.set(c.id, c);
  for (const c of item?.contas ?? []) if (!contaPorId.has(c.id)) contaPorId.set(c.id, c);
  const ligadas = valor.contaIds.map((id) => contaPorId.get(id)).filter((c): c is ContaContabilResponse => c !== undefined);

  function ligarConta(id: string) {
    if (valor.contaIds.includes(id)) return;
    setValor((v) => ({ ...v, contaIds: [...v.contaIds, id] }));
  }

  function desligarConta(id: string) {
    setValor((v) => ({ ...v, contaIds: v.contaIds.filter((c) => c !== id) }));
    if (contaEmEdicao?.id === id) setContaEmEdicao(null);
  }

  function ligarExistente() {
    if (!contaEscolhida) return;
    ligarConta(contaEscolhida);
    setContaEscolhida('');
    setModoAdicionar(null);
  }

  function incluirContaNova() {
    const montado = montarCorpoConta({ codigo: novaConta.codigo, descricao: novaConta.descricao, ativo: true }, true);
    if (!montado.ok) {
      setNovaConta((n) => ({ ...n, erro: montado.erro.mensagem }));
      return;
    }
    setContasNovas((l) => [...l, { tmp: `nova-${Date.now()}-${l.length}`, codigo: novaConta.codigo.trim(), descricao: novaConta.descricao.trim(), ativo: true, criadoId: null, erro: null }]);
    setNovaConta({ codigo: '', descricao: '', erro: null });
    setModoAdicionar(null);
  }

  function iniciarEdicao(c: ContaContabilResponse) {
    setContaEmEdicao({ id: c.id, codigo: c.codigo, descricao: c.descricao, erro: null });
  }

  async function salvarEdicao() {
    const e = contaEmEdicao;
    if (!e) return;
    const conta = contaPorId.get(e.id);
    const montado = montarCorpoConta({ codigo: e.codigo, descricao: e.descricao, ativo: conta?.ativo ?? true }, false);
    if (!montado.ok) {
      setContaEmEdicao({ ...e, erro: montado.erro.mensagem });
      return;
    }
    try {
      await atualizarConta.mutateAsync({ id: e.id, dto: montado.corpo as AtualizarContaContabilDto });
      setContaEmEdicao(null);
    } catch (err) {
      setContaEmEdicao({ ...e, erro: mensagemConta(err) });
    }
  }

  function atualizarNova(tmp: string, mudanca: Partial<ContaNova>) {
    setContasNovas((l) => l.map((n) => (n.tmp === tmp ? { ...n, ...mudanca } : n)));
  }

  async function gravar() {
    setErroGeral(null);
    setErroCampo(null);
    setErroApropriacao(null);
    if (valor.destino === 'apropriacao' && apropriacao && !apropriacao.completa) {
      setErroApropriacao({ code: 'APROPRIACAO_INCOMPLETA', message: 'A apropriação precisa fechar 100% (e ter todas as linhas completas) para salvar.' });
      return;
    }
    const opcoes = {
      criacao,
      apropriacao: apropriacao ? apropriacao.valor : item ? apropriacaoInicial(item.apropriacao) : null,
      apropriacaoAlterada: apropriacao !== null,
    };
    // Valida o item antes de criar contas, para nao deixar conta criada com o formulario invalido.
    const previa = montarCorpoItem(valor, opcoes);
    if (!previa.ok) {
      setErroCampo(previa.erro);
      return;
    }

    // Contas novas: POST uma a uma. As ja criadas (criadoId) nao sao criadas de novo em nova tentativa.
    const idsNovas: string[] = [];
    for (const n of contasNovas) {
      if (n.criadoId) {
        idsNovas.push(n.criadoId);
        continue;
      }
      const montado = montarCorpoConta({ codigo: n.codigo, descricao: n.descricao, ativo: true }, true);
      if (!montado.ok) {
        atualizarNova(n.tmp, { erro: montado.erro.mensagem });
        return;
      }
      try {
        const criada = await criarConta.mutateAsync(montado.corpo as CriarContaContabilDto);
        idsNovas.push(criada.id);
        atualizarNova(n.tmp, { criadoId: criada.id, erro: null });
      } catch (e) {
        atualizarNova(n.tmp, { erro: mensagemConta(e) });
        return;
      }
    }

    const montado = montarCorpoItem({ ...valor, contaIds: [...valor.contaIds, ...idsNovas] }, opcoes);
    if (!montado.ok) {
      setErroCampo(montado.erro);
      return;
    }
    try {
      if (item) await atualizar.mutateAsync({ id: item.id, dto: montado.corpo as AtualizarItemCifDto });
      else await criar.mutateAsync(montado.corpo as CriarItemCifDto);
      onFechar();
    } catch (e) {
      setErroGeral(mensagemApi(e));
      if (e instanceof ApiError && (e.code?.includes('SOMA') || e.code?.startsWith('APROPRIACAO'))) {
        setErroApropriacao({ code: e.code ?? 'APROPRIACAO', message: e.message });
      }
    }
  }

  const disponiveis = (contas.data ?? []).filter((c) => c.ativo);
  const linhasVazias = ligadas.length === 0 && contasNovas.length === 0;

  return (
    <Dialog open onOpenChange={(o) => !o && !salvando && onFechar()}>
      <DialogContent className="sm:max-w-[760px] sm:min-w-[720px] w-[calc(100vw-2rem)] max-h-[calc(100vh-2rem)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{criacao ? 'Novo item de CIF' : 'Item de CIF'}</DialogTitle>
          <DialogDescription>Lançamento recorrente: ao gerar o mês, cria a CIF com a apropriação padrão deste item.</DialogDescription>
        </DialogHeader>

        <fieldset disabled={!podeEditar || salvando} className="flex flex-col gap-4">
          <div>
            <label htmlFor="item-cif-nome" className={ROTULO}>Nome</label>
            <input id="item-cif-nome" maxLength={120} className={CAMPO} value={valor.nome} aria-invalid={erroCampo?.campo === 'nome' ? true : undefined} onChange={(e) => setValor((v) => ({ ...v, nome: e.target.value }))} />
            {erroCampo?.campo === 'nome' && <p role="alert" className="mt-1 text-caption text-danger-ink">{erroCampo.mensagem}</p>}
          </div>

          {!criacao && (
            <label className="flex items-center gap-2 text-small text-ink-body">
              <input type="checkbox" checked={valor.ativo} onChange={(e) => setValor((v) => ({ ...v, ativo: e.target.checked }))} />
              Ativo (inativo não entra em &quot;Gerar lançamentos do mês&quot;)
            </label>
          )}

          <fieldset>
            <legend className={ROTULO}>Contas contábeis vinculadas</legend>
            {linhasVazias ? (
              <p className="text-small text-ink-muted">Nenhuma conta contábil vinculada.</p>
            ) : (
              <ul aria-label="Contas vinculadas" className="grid gap-1.5 rounded-[9px] border border-input p-2.5">
                {ligadas.map((c) => (
                  <li key={c.id} data-testid="conta-ligada" className="flex flex-col gap-1.5">
                    {contaEmEdicao?.id === c.id ? (
                      <div className="grid gap-2 sm:grid-cols-[160px_1fr_auto] sm:items-start">
                        <label className="sr-only" htmlFor={`conta-edit-codigo-${c.id}`}>Código da conta</label>
                        <input id={`conta-edit-codigo-${c.id}`} maxLength={40} className={CAMPO} value={contaEmEdicao.codigo} onChange={(e) => setContaEmEdicao({ ...contaEmEdicao, codigo: e.target.value, erro: null })} />
                        <label className="sr-only" htmlFor={`conta-edit-descricao-${c.id}`}>Descrição da conta</label>
                        <input id={`conta-edit-descricao-${c.id}`} maxLength={200} className={CAMPO} value={contaEmEdicao.descricao} onChange={(e) => setContaEmEdicao({ ...contaEmEdicao, descricao: e.target.value, erro: null })} />
                        <div className="flex gap-2">
                          <Button type="button" size="sm" onClick={() => void salvarEdicao()} disabled={atualizarConta.isPending}>Salvar conta</Button>
                          <Button type="button" size="sm" variant="outline" onClick={() => setContaEmEdicao(null)}>Cancelar</Button>
                        </div>
                        {contaEmEdicao.erro && <p role="alert" className="text-caption text-danger-ink sm:col-span-3">{contaEmEdicao.erro}</p>}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center justify-between gap-2 text-small text-ink-body">
                        <span>
                          <span className="font-mono font-semibold">{c.codigo}</span> — {c.descricao}
                          {!c.ativo && <span className="text-ink-muted"> (inativa)</span>}
                        </span>
                        <span className="flex gap-1">
                          <Button type="button" size="sm" variant="ghost" onClick={() => iniciarEdicao(c)}>Editar</Button>
                          <Button type="button" size="sm" variant="ghost" onClick={() => desligarConta(c.id)}>Remover</Button>
                        </span>
                      </div>
                    )}
                  </li>
                ))}
                {contasNovas.map((n) => (
                  <li key={n.tmp} data-testid="conta-nova-ligada" className="flex flex-col gap-1 text-small text-ink-body">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        <span className="font-mono font-semibold">{n.codigo}</span> — {n.descricao} <span className="text-ink-muted">({n.criadoId ? 'criada' : 'nova, criada ao gravar'})</span>
                      </span>
                      <Button type="button" size="sm" variant="ghost" onClick={() => setContasNovas((l) => l.filter((x) => x.tmp !== n.tmp))}>Remover</Button>
                    </div>
                    {n.erro && <p role="alert" className="text-caption text-danger-ink">{n.erro}</p>}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-2 flex flex-col gap-2">
              {modoAdicionar === null ? (
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" variant="outline" onClick={() => setModoAdicionar('existente')}>Adicionar conta existente</Button>
                  <Button type="button" size="sm" variant="outline" onClick={() => setModoAdicionar('nova')}>Criar conta nova</Button>
                </div>
              ) : modoAdicionar === 'existente' ? (
                <div className="flex flex-wrap items-end gap-2">
                  <div className="min-w-[260px] flex-1">
                    <label htmlFor="item-cif-conta-existente" className={ROTULO}>Conta existente</label>
                    <select id="item-cif-conta-existente" className={CAMPO} value={contaEscolhida} onChange={(e) => setContaEscolhida(e.target.value)}>
                      <option value="">Escolha a conta</option>
                      {disponiveis.map((c) => {
                        const ligada = valor.contaIds.includes(c.id);
                        return (
                          <option key={c.id} value={c.id} disabled={ligada}>
                            {`${c.codigo} — ${c.descricao}${ligada ? ' (já ligada)' : ''}`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <Button type="button" size="sm" onClick={ligarExistente} disabled={!contaEscolhida}>Ligar conta</Button>
                  <Button type="button" size="sm" variant="ghost" onClick={() => setModoAdicionar(null)}>Cancelar</Button>
                </div>
              ) : (
                <div className="grid gap-2 sm:grid-cols-[160px_1fr_auto] sm:items-end">
                  <div>
                    <label htmlFor="item-cif-nova-codigo" className={ROTULO}>Código da nova conta</label>
                    <input id="item-cif-nova-codigo" maxLength={40} className={CAMPO} value={novaConta.codigo} onChange={(e) => setNovaConta({ ...novaConta, codigo: e.target.value, erro: null })} />
                  </div>
                  <div>
                    <label htmlFor="item-cif-nova-descricao" className={ROTULO}>Descrição da nova conta</label>
                    <input id="item-cif-nova-descricao" maxLength={200} className={CAMPO} value={novaConta.descricao} onChange={(e) => setNovaConta({ ...novaConta, descricao: e.target.value, erro: null })} />
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" onClick={incluirContaNova}>Incluir conta nova</Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => setModoAdicionar(null)}>Cancelar</Button>
                  </div>
                  {novaConta.erro && <p role="alert" className="text-caption text-danger-ink sm:col-span-3">{novaConta.erro}</p>}
                </div>
              )}
            </div>
          </fieldset>

          <fieldset>
            <legend className={ROTULO}>Destino do valor</legend>
            <div className="flex flex-wrap gap-4 text-small text-ink-body">
              <label className="flex items-center gap-2">
                <input type="radio" name="item-destino" checked={valor.destino === 'apropriacao'} onChange={() => setValor((v) => ({ ...v, destino: 'apropriacao' }))} />
                Apropriação padrão
              </label>
              <label className="flex items-center gap-2">
                <input type="radio" name="item-destino" checked={valor.destino === 'centro'} onChange={() => setValor((v) => ({ ...v, destino: 'centro' }))} />
                Vai direto para o centro
              </label>
            </div>
          </fieldset>

          {valor.destino === 'centro' ? (
            <div>
              <label htmlFor="item-cif-centro" className={ROTULO}>Centro (100% do valor)</label>
              <select id="item-cif-centro" className={CAMPO} value={valor.centroId} aria-invalid={erroCampo?.campo === 'centroId' ? true : undefined} onChange={(e) => setValor((v) => ({ ...v, centroId: e.target.value }))}>
                <option value="">Escolha o centro</option>
                {(centros.data ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${c.caminho} — ${c.nome}`}
                  </option>
                ))}
              </select>
              {erroCampo?.campo === 'centroId' && <p role="alert" className="mt-1 text-caption text-danger-ink">{erroCampo.mensagem}</p>}
            </div>
          ) : (
            <ApropriacaoPorCentro
              value={item ? apropriacaoInicial(item.apropriacao) : null}
              onChange={(v, situacao) => setApropriacao({ valor: v, completa: situacao?.completa ?? v !== null })}
              resolvida={item?.apropriacao ?? null}
              referTo="CIF"
              disabled={!podeEditar || salvando}
              erro={erroApropriacao}
            />
          )}
        </fieldset>

        {erroGeral && <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-small text-danger-ink">{erroGeral}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={onFechar} disabled={salvando}>
            {podeEditar ? 'Cancelar' : 'Fechar'}
          </Button>
          {podeEditar && (
            <Button onClick={() => void gravar()} disabled={salvando}>
              {salvando ? 'Gravando…' : 'Gravar'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
